import React, { useContext, useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Form, InputGroup, Modal } from 'react-bootstrap';
import {
  FaPlus, FaSearch, FaEye, FaTicketAlt, FaCheckCircle, FaClock,
  FaExclamationCircle, FaCalendarAlt, FaUser, FaEnvelope, FaPaperPlane
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../../assets/css/admin/dashboard/SupportTickets.css';
import AdminLayout from '../../../layouts/admin/AdminLayout';
import AppContext from '../../../context/AppContext';
import axios from 'axios';

const SupportTickets = () => {

  const [tickets,         setTickets]         = useState([]);
  const [filters,         setFilters]         = useState({ search: '', status: 'all' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal,   setShowViewModal]   = useState(false);
  const [selectedTicket,  setSelectedTicket]  = useState(null);
  const [replyMessage,    setReplyMessage]    = useState('');
  const [replyStatus,     setReplyStatus]     = useState('');
  const [isLoading,       setIsLoading]       = useState(false);

  // ── NEW: page-level loading (same pattern as Blogs Management / Event Types) ──
  const [isTicketsLoading, setIsTicketsLoading] = useState(true);

  // ── NEW: button loading for Create New Ticket (same pattern as Blogs' New Post) ──
  const [navLoading, setNavLoading] = useState(null);

  const [chatMessages,    setChatMessages]    = useState([]);
  const chatEndRef = useRef(null);

  const [newTicket, setNewTicket] = useState({
    bookingId: '', customerName: '',
    category: '', priority: '', subject: '', description: ''
  });

  // ===================== CREATE TICKET VALIDATION ERRORS (regexChecker based) =====================
  const [ticketErrors, setTicketErrors] = useState({
    customerName: '', category: '', priority: '', subject: '', description: ''
  });

  const { baseUrl, regexChecker } = useContext(AppContext);

  const viewAllTickets = () => {
    setIsTicketsLoading(true);
    axios.get(`${baseUrl}/admin/helpSupport/viewAllTickets`)
    .then((res) => {
      if (res.data.success === true) setTickets(res.data.tickets);
    })
    .catch((err) => console.log(err))
    .finally(() => setIsTicketsLoading(false));
  };

  useEffect(() => { viewAllTickets(); }, []);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // ===================== VALIDATION (same style as CustomerProfile / MyEvents / HelpandSupport) =====================
  const resetTicketErrors = () => {
    setTicketErrors({ customerName: '', category: '', priority: '', subject: '', description: '' });
  };

  const handleOpenCreateModal = () => {
    setNavLoading('create');
    setTimeout(() => {
      resetTicketErrors();
      setShowCreateModal(true);
      setNavLoading(null);
    }, 600);
  };

  const validateNewTicketForm = () => {
    const errors = {
      customerName: regexChecker('fullName', newTicket.customerName),
      category:     newTicket.category ? '' : 'Category is required',
      priority:     newTicket.priority ? '' : 'Priority is required',
      subject:      regexChecker('subject', newTicket.subject),
      description:  regexChecker('description', newTicket.description)
    };

    setTicketErrors(errors);

    return Object.values(errors).some((err) => err);
  };

  const handleSubmitTicket = () => {
    const hasErrors = validateNewTicketForm();
    if (hasErrors) {
      Swal.fire({ icon: 'warning', title: 'Required Fields', text: 'Please fix the highlighted fields', confirmButtonColor: '#7fd3c1' });
      return;
    }
    setIsLoading(true);
    const loggedInAdmin = JSON.parse(localStorage.getItem('adminData'));
    const formData = new FormData();
    formData.append('weadmin_id',   loggedInAdmin.id);
    formData.append('webookingId',  newTicket.bookingId);
    formData.append('customerName', newTicket.customerName);
    formData.append('category',     newTicket.category);
    formData.append('priority',     newTicket.priority);
    formData.append('subject',      newTicket.subject);
    formData.append('description',  newTicket.description);
    axios.post(`${baseUrl}/admin/helpSupport/createTicket`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then((res) => {
      if (res.data.success === true) {
        viewAllTickets();
        setShowCreateModal(false);
        setNewTicket({ bookingId: '', customerName: '', category: '', priority: '', subject: '', description: '' });
        resetTicketErrors();
        Swal.fire({ icon: 'success', title: 'Ticket Created!', timer: 2000, showConfirmButton: false });
      }
    })
    .catch((err) => console.log(err))
    .finally(() => setIsLoading(false));
  };

  const handleSendReply = () => {
    if (!replyMessage.trim()) {
      Swal.fire({ icon: 'warning', title: 'Empty Message', text: 'Please enter a reply message', confirmButtonColor: '#7fd3c1' });
      return;
    }
    const formData = new FormData();
    formData.append('id',         selectedTicket.id);
    formData.append('adminReply', replyMessage);
    formData.append('status',     replyStatus || selectedTicket.status);
    axios.post(`${baseUrl}/admin/helpSupport/updateTicket`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then((res) => {
      if (res.data.success === true) {
        const newMsg = {
          id: chatMessages.length + 1,
          sender: 'admin',
          text: replyMessage.trim(),
          time: new Date().toISOString().slice(0, 10),
        };
        setChatMessages((prev) => [...prev, newMsg]);
        setReplyMessage('');
        viewAllTickets();
        Swal.fire({ icon: 'success', title: 'Reply Sent!', timer: 2000, showConfirmButton: false });
      }
    })
    .catch((err) => console.log(err));
  };

  const handleDeleteTicket = (id) => {
    Swal.fire({
      title: 'Delete Ticket?', icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#f56565', cancelButtonColor: '#718096', confirmButtonText: 'Yes, delete!'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(`${baseUrl}/admin/helpSupport/deleteTicket/${id}`)
        .then((res) => {
          if (res.data.success === true) {
            viewAllTickets();
            Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
          }
        })
        .catch((err) => console.log(err));
      }
    });
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setReplyStatus(ticket.status);
    const msgs = [];
    if (ticket.description) {
      msgs.push({ id: 1, sender: 'customer', text: ticket.description, time: ticket.created_at ? ticket.created_at.slice(0, 10) : '' });
    }
    if (ticket.adminReply) {
      msgs.push({ id: 2, sender: 'admin', text: ticket.adminReply, time: ticket.updated_at ? ticket.updated_at.slice(0, 10) : '' });
    }
    setChatMessages(msgs);
    setShowViewModal(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendReply(); }
  };

  const handleFilterChange = (field, value) => setFilters({ ...filters, [field]: value });

  const getStatusBadge = (status) => {
    const config = {
      'Open':    { bg: 'success',   icon: <FaExclamationCircle /> },
      'Pending': { bg: 'warning',   icon: <FaClock /> },
      'Closed':  { bg: 'secondary', icon: <FaCheckCircle /> }
    };
    return config[status] || config['Open'];
  };

  const getPriorityBadge = (priority) => {
    const config = { 'High': { bg: 'danger' }, 'Medium': { bg: 'warning' }, 'Low': { bg: 'info' } };
    return config[priority] || config['Medium'];
  };

  const viewBtnStyle = {
    width: '42px', height: '42px', padding: '0',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: '8px', border: 'none', cursor: 'pointer',
    fontSize: '16px', color: '#ffffff',
    background: 'linear-gradient(135deg, #3b82f6, #60a5fa)',
    transition: 'transform 0.18s ease',
  };

  const stats = [
    { id: 1, title: 'Total Tickets', value: tickets.length,                                    icon: <FaTicketAlt />,        gradient: 'linear-gradient(135deg, #3b82f6, #60a5fa)' },
    { id: 2, title: 'Open',          value: tickets.filter(t => t.status === 'Open').length,    icon: <FaExclamationCircle />, gradient: 'linear-gradient(135deg, #10b981, #34d399)' },
    { id: 3, title: 'Pending',       value: tickets.filter(t => t.status === 'Pending').length, icon: <FaClock />,             gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)' },
    { id: 4, title: 'Closed',        value: tickets.filter(t => t.status === 'Closed').length,  icon: <FaCheckCircle />,       gradient: 'linear-gradient(135deg, #6366f1, #818cf8)' }
  ];

  const filteredTickets = tickets.filter((t) => {
    const matchSearch = filters.search === '' || String(t.id).includes(filters.search);
    const matchStatus = filters.status === 'all' || t.status === filters.status;
    return matchSearch && matchStatus;
  });

  return (
    <AdminLayout>
      <div className="st-support-page">
        <Container fluid>

          {/* Header */}
          <div className="st-page-header">
            <div className="st-header-content">
              <h1 className="st-page-title">Support Tickets</h1>
              <p className="st-page-subtitle">Manage and track your support requests</p>
            </div>
            <Button className="st-btn-create" onClick={handleOpenCreateModal} disabled={navLoading === 'create'}>
              {navLoading === 'create' ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Loading...
                </>
              ) : (
                <>
                  <FaPlus className="me-2" />Create New Ticket
                </>
              )}
            </Button>
          </div>

          {/* Stats */}
          <Row className="st-stats-row">
            {stats.map((stat) => (
              <Col key={stat.id} lg={3} md={6} sm={12} className="mb-4">
                <Card className="st-stat-card" style={{ background: stat.gradient }}>
                  <Card.Body className="st-stat-body">
                    <div className="st-stat-icon">{stat.icon}</div>
                    <div className="st-stat-content">
                      <h3 className="st-stat-value">{stat.value}</h3>
                      <p className="st-stat-title">{stat.title}</p>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Filters */}
          <Card className="st-filters-card">
            <Card.Body className="st-filters-body">
              <Row>
                <Col lg={3} md={6} sm={12} className="mb-3">
                  <InputGroup className="st-search-group">
                    <InputGroup.Text className="st-search-icon"><FaSearch /></InputGroup.Text>
                    <Form.Control type="text" placeholder="Search Ticket ID..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="st-filter-input" />
                  </InputGroup>
                </Col>
                <Col lg={3} md={6} sm={12} className="mb-3">
                  <Form.Select value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="st-filter-select">
                    <option value="all">Filter by Status</option>
                    <option value="Open">Open</option>
                    <option value="Pending">Pending</option>
                    <option value="Closed">Closed</option>
                  </Form.Select>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Table */}
          <Card className="st-table-card">
            <Card.Body className="st-table-body">
              {isTicketsLoading ? (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <div className="spinner-border" role="status" style={{ color: '#7fd3c1' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p style={{ marginTop: '12px', color: '#888' }}>Loading tickets...</p>
                </div>
              ) : (
              <div className="st-table-wrapper">
                <Table responsive className="st-table">
                  <thead className="st-table-head">
                    <tr>
                      <th>Ticket ID</th>
                      <th>Booking ID</th>
                      <th>Category</th>
                      <th>Priority</th>
                      <th>Subject</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody className="st-table-body-content">
                    {filteredTickets.length > 0 ? filteredTickets.map((ticket) => (
                      <tr key={ticket.id}>
                        <td className="st-ticket-id">#{ticket.id}</td>
                        <td className="st-event-name">BK-{ticket.webookingId}</td>
                        <td className="st-category">{ticket.category}</td>
                        <td>
                          <Badge bg={getPriorityBadge(ticket.priority).bg} className="st-priority-badge">
                            {ticket.priority}
                          </Badge>
                        </td>
                        <td>{ticket.subject}</td>
                        <td>
                          <Badge bg={getStatusBadge(ticket.status).bg} className="st-status-badge">
                            {getStatusBadge(ticket.status).icon}
                            <span className="ms-2">{ticket.status}</span>
                          </Badge>
                        </td>
                        <td>
                          <button style={viewBtnStyle} onClick={() => handleViewTicket(ticket)}>
                            <FaEye />
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="7" className="text-center py-4">No tickets found</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
              )}
            </Card.Body>
          </Card>

        </Container>

        {/* ── Create Modal ── */}
        <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered className="st-modal">
          <Modal.Header className="st-modal-header">
            <Modal.Title className="st-modal-title"><FaTicketAlt className="me-2" />Create Support Ticket</Modal.Title>
            <button className="st-modal-close" onClick={() => setShowCreateModal(false)}>×</button>
          </Modal.Header>
          <Modal.Body className="st-modal-body">
            <Row>
              <Col md={6} className="mb-3">
                <Form.Label className="st-form-label">Booking ID</Form.Label>
                <Form.Control type="text" value={newTicket.bookingId}
                  onChange={(e) => setNewTicket({ ...newTicket, bookingId: e.target.value })}
                  placeholder="Booking ID" className="st-form-input" />
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label className="st-form-label">Customer Name *</Form.Label>
                <Form.Control type="text" value={newTicket.customerName}
                  onChange={(e) => {
                    setNewTicket({ ...newTicket, customerName: e.target.value });
                    setTicketErrors({ ...ticketErrors, customerName: regexChecker('fullName', e.target.value) });
                  }}
                  placeholder="Enter customer name"
                  className={`st-form-input ${ticketErrors.customerName ? 'input-error' : ''}`} />
                {ticketErrors.customerName && (
                  <p style={{ color: 'red', fontSize: '13px' }}>{ticketErrors.customerName}</p>
                )}
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label className="st-form-label">Category *</Form.Label>
                <Form.Select value={newTicket.category}
                  onChange={(e) => {
                    setNewTicket({ ...newTicket, category: e.target.value });
                    setTicketErrors({ ...ticketErrors, category: e.target.value ? '' : 'Category is required' });
                  }}
                  className={`st-form-input ${ticketErrors.category ? 'input-error' : ''}`}>
                  <option value="">Select Category</option>
                  <option value="Payment">Payment</option>
                  <option value="Venue">Venue</option>
                  <option value="Catering">Catering</option>
                  <option value="Decor">Decor</option>
                  <option value="Other">Other</option>
                </Form.Select>
                {ticketErrors.category && (
                  <p style={{ color: 'red', fontSize: '13px' }}>{ticketErrors.category}</p>
                )}
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label className="st-form-label">Priority *</Form.Label>
                <Form.Select value={newTicket.priority}
                  onChange={(e) => {
                    setNewTicket({ ...newTicket, priority: e.target.value });
                    setTicketErrors({ ...ticketErrors, priority: e.target.value ? '' : 'Priority is required' });
                  }}
                  className={`st-form-input ${ticketErrors.priority ? 'input-error' : ''}`}>
                  <option value="">Select Priority</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </Form.Select>
                {ticketErrors.priority && (
                  <p style={{ color: 'red', fontSize: '13px' }}>{ticketErrors.priority}</p>
                )}
              </Col>
              <Col md={12} className="mb-3">
                <Form.Label className="st-form-label">Subject *</Form.Label>
                <Form.Control type="text" value={newTicket.subject}
                  onChange={(e) => {
                    setNewTicket({ ...newTicket, subject: e.target.value });
                    setTicketErrors({ ...ticketErrors, subject: regexChecker('subject', e.target.value) });
                  }}
                  placeholder="Enter ticket subject"
                  className={`st-form-input ${ticketErrors.subject ? 'input-error' : ''}`} />
                {ticketErrors.subject && (
                  <p style={{ color: 'red', fontSize: '13px' }}>{ticketErrors.subject}</p>
                )}
              </Col>
              <Col md={12} className="mb-3">
                <Form.Label className="st-form-label">Description *</Form.Label>
                <Form.Control as="textarea" rows={4} value={newTicket.description}
                  onChange={(e) => {
                    setNewTicket({ ...newTicket, description: e.target.value });
                    setTicketErrors({ ...ticketErrors, description: regexChecker('description', e.target.value) });
                  }}
                  placeholder="Describe your issue..."
                  className={`st-form-textarea ${ticketErrors.description ? 'input-error' : ''}`} />
                {ticketErrors.description && (
                  <p style={{ color: 'red', fontSize: '13px' }}>{ticketErrors.description}</p>
                )}
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="st-modal-footer">
            <Button className="st-btn-cancel" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button className="st-btn-submit" onClick={handleSubmitTicket} disabled={isLoading}>
              <FaPaperPlane className="me-2" />{isLoading ? 'Submitting...' : 'Submit Ticket'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ── View Modal with Chat ── */}
        <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered className="st-modal">
          <Modal.Header className="st-modal-header">
            <Modal.Title className="st-modal-title">
              <FaTicketAlt className="me-2" />Ticket Details - #{selectedTicket?.id}
            </Modal.Title>
            <button className="st-modal-close" onClick={() => setShowViewModal(false)}>×</button>
          </Modal.Header>
          <Modal.Body className="st-modal-body">
            {selectedTicket && (
              <>
                {/* Ticket Info */}
                <div className="st-ticket-info">
                  <Row>
                    <Col md={4} className="mb-3">
                      <span className="st-info-label">Status: </span>
                      {/* ✅ Status dropdown upar move kiya */}
                      <Form.Select value={replyStatus}
                        onChange={(e) => setReplyStatus(e.target.value)}
                        className="st-form-input mt-1" size="sm">
                        <option value="Open">Open</option>
                        <option value="Pending">Pending</option>
                        <option value="Closed">Closed</option>
                      </Form.Select>
                    </Col>
                    <Col md={4} className="mb-3">
                      <span className="st-info-label">Priority: </span>
                      <Badge bg={getPriorityBadge(selectedTicket.priority).bg}>{selectedTicket.priority}</Badge>
                    </Col>
                    <Col md={4} className="mb-3">
                      <span className="st-info-label">Booking ID: </span>
                      <span className="st-info-value">BK-{selectedTicket.webookingId}</span>
                    </Col>
                    <Col md={6} className="mb-2">
                      <span className="st-info-label">Category: </span>
                      <span className="st-info-value">{selectedTicket.category}</span>
                    </Col>
                    <Col md={6} className="mb-2">
                      <span className="st-info-label">Subject: </span>
                      <span className="st-info-value">{selectedTicket.subject}</span>
                    </Col>
                  </Row>
                </div>

                {/* ── Chat History ── */}
                <div className="st-chat-section">
                  <h6 className="st-chat-title">Chat History</h6>
                  <div className="st-chat-messages">
                    {chatMessages.length === 0 ? (
                      <div className="st-chat-empty">No messages yet</div>
                    ) : (
                      chatMessages.map((msg) => (
                        <div key={msg.id} className={`st-chat-msg-wrapper ${msg.sender === 'admin' ? 'st-msg-right' : 'st-msg-left'}`}>
                          <span className="st-chat-sender">
                            {msg.sender === 'admin' ? 'Support Team' : 'Customer'}
                          </span>
                          <div className={`st-chat-bubble ${msg.sender === 'admin' ? 'st-bubble-admin' : 'st-bubble-customer'}`}>
                            {msg.text}
                          </div>
                          <span className="st-chat-time">{msg.time}</span>
                        </div>
                      ))
                    )}
                    <div ref={chatEndRef} />
                  </div>
                </div>

                {/* ── Reply Section ── */}
                {/* ✅ Update Status wala hissa hata diya */}
                <div className="st-reply-section">
                  <Form.Label className="st-form-label">Reply:</Form.Label>
                  <div className="st-reply-input-bar">
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Type your reply... (Enter to send)"
                      className="st-form-textarea st-reply-textarea"
                    />
                    <Button className="st-btn-send-reply mt-2" onClick={handleSendReply}>
                      <FaPaperPlane className="me-2" />Send Reply
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Modal.Body>
        </Modal>

      </div>
    </AdminLayout>
  );
};

export default SupportTickets;